import { CargoType, CreateRailVisitCommand, UpdateRailVisitCommand } from '@planning/app/api'
import { ITripId } from '@planning/components/visit/VisitTripIdsFields'
import { RailVisitFormStoreType } from '@planning/pages/RailVisit/stores/RaillVisitFormStore'
import { railVisitService } from '@planning/services'
import { getDateTimeUtcString } from '@tom-ui/utils'
import { Dayjs } from 'dayjs'
import { action, computed, makeObservable, observable } from 'mobx'
import { VisitPlanningStore } from '../visit/VisitPlanningStore'
import { IRailVisitItem } from './RailVisitItem'

export interface IRailVisitPlanningFormData {
  id?: number
  eta?: Dayjs
  name: string
  etd?: Dayjs
  trainOperator?: string
  cargoType: CargoType
  dischargeEstimate: number
  loadEstimate: number
  railcarEstimate?: number
  railTrackIds: string[]
  tripIds: ITripId[]
}

export class RailVisitPlanningStore extends VisitPlanningStore {
  railVisit?: IRailVisitItem

  constructor() {
    super()
    makeObservable(this, {
      railVisit: observable,

      setRailVisit: action,
      createRailVisit: action,
      reset: action,

      railTrackIdsWithOrders: computed,
    })
  }

  createRailVisit = async (form: RailVisitFormStoreType) => {
    const createCmd: CreateRailVisitCommand = {
      ...this.mapToCommandFromFormStore(form),
      schedule: this.scheduleTiming(),
    }

    return await railVisitService.post(createCmd)
  }

  updateRailVisit = async (id: number, form: RailVisitFormStoreType) => {
    if (!id) return

    const updateCmd: UpdateRailVisitCommand = {
      ...this.mapToCommandFromFormStore(form),
      id,
    }

    return await railVisitService.put(updateCmd)
  }

  mapToCommandFromFormStore(form: RailVisitFormStoreType) {
    const { generalInformation, cargo, trackAndTiming } = form

    const etaUtc = getDateTimeUtcString(trackAndTiming?.etaDate, trackAndTiming?.etaTime)
    const etdUtc = getDateTimeUtcString(trackAndTiming?.etdDate, trackAndTiming?.etdTime)

    return {
      name: generalInformation?.trainName ?? 'Unknown Train',
      eta: etaUtc,
      etd: etdUtc,
      inboundTripIds:
        generalInformation?.voyages?.filter(v => v.inboundNumber).map(v => v.inboundNumber!) ?? [],
      outboundTripIds:
        generalInformation?.voyages?.filter(v => v.outboundNumber).map(v => v.outboundNumber!) ??
        [],
      loadEstimate: cargo?.loadEstimate ? parseInt(cargo.loadEstimate.toString()) : 0,
      dischargeEstimate: cargo?.dischargeEstimate
        ? parseInt(cargo.dischargeEstimate.toString())
        : 0,
      cargoType: cargo?.cargoType ?? CargoType.Container,
      railTrackIds: trackAndTiming?.railTracks?.map(r => r.id) ?? [],
      railcarEstimate: trackAndTiming?.railcarEstimate,
      trainOperator: generalInformation?.trainOperator,
    }
  }

  setRailVisit = (visit?: IRailVisitItem) => {
    this.railVisit = visit
  }

  reset = () => {
    this.railVisit = undefined

    this.resetVisit()
  }

  get railTrackIdsWithOrders() {
    const trackIdsWithOrders = this.railVisit?.discharge.containerOrders
      .filter(x => x.railcarTrackPosition?.railTrack?.id)
      .map(x => x.railcarTrackPosition!.railTrack!.id)

    this.railVisit?.load.containerOrders.forEach(x => {
      const trackId = x.railcarTrackPosition?.railTrack?.id
      if (trackId && !trackIdsWithOrders?.includes(trackId)) {
        trackIdsWithOrders?.push(trackId)
      }
    })

    return trackIdsWithOrders
  }

  get railTrackWithDuplicateRailcars() {
    const railcarMap = new Map<string, Map<number, Set<string>>>()
    const railcarTracks = this.railVisit?.railcarTrackPositions.map(x => x.data) ?? []

    for (const { railcarId, railTrackId, direction } of railcarTracks) {
      if (!railcarMap.has(direction)) {
        railcarMap.set(direction, new Map<number, Set<string>>())
      }

      const directionMap = railcarMap.get(direction)!

      if (!directionMap.has(railcarId)) {
        directionMap?.set(railcarId, new Set<string>())
      }

      directionMap.get(railcarId)!.add(railTrackId)
    }

    return Array.from(railcarMap.values()).some(directionMap =>
      Array.from(directionMap.values()).some(railTrackIds => railTrackIds.size > 1),
    )
  }
}
